import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiClient } from '@/services/api';

interface User {
  id: string;
  email: string;
  name: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (emailOrUsername: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount - initialize auth state synchronously
  useEffect(() => {
    const storedUser = localStorage.getItem('auth_user');
    const storedAuthVersion = localStorage.getItem('auth_version');
    const currentAuthVersion = import.meta.env.VITE_AUTH_VERSION || '1';

    // If auth version has changed, force re-login by clearing stored data
    if (storedAuthVersion !== currentAuthVersion) {
      localStorage.removeItem('auth_user');
      localStorage.removeItem('auth_version');
      setIsLoading(false);
      return;
    }

    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        // Batch both state updates together to prevent intermediate renders
        setUser(parsedUser);
        setIsLoading(false);
        return;
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('auth_user');
        localStorage.removeItem('auth_version');
      }
    }
    setIsLoading(false);
  }, []);

  // Set up ApiClient when user authentication state changes
  useEffect(() => {
    if (user) {
      // Set up a simple token provider - for now we'll use a static token
      // In a real app, this would get a JWT token from the backend
      apiClient.setAuthProvider(async () => {
        // For the datasets app, we'll create a simple token from user data
        // This is a placeholder - in production you'd get a real JWT from your backend
        return `dataset-token-${user.id}`;
      });
      
      // Set the user for headers
      apiClient.setUser(user);
    }
  }, [user]);

  const login = async (emailOrUsername: string, password: string) => {
    setIsLoading(true);
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Check against environment password
    const envPassword = import.meta.env.VITE_APP_PASSWORD;
    if (password !== envPassword) {
      setIsLoading(false);
      throw new Error('Invalid password');
    }
    
    // Create authenticated user
    const authenticatedUser: User = {
      id: `user_${Date.now()}`,
      email: 'admin@silentwitness.com',
      name: 'Admin User'
    };

    setUser(authenticatedUser);
    localStorage.setItem('auth_user', JSON.stringify(authenticatedUser));

    // Store current auth version to track when password changes
    const currentAuthVersion = import.meta.env.VITE_AUTH_VERSION || '1';
    localStorage.setItem('auth_version', currentAuthVersion);

    setIsLoading(false);
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_version');
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}